/**
 * /account/subscription — the reader-app posture's "Manage subscription
 * on the web" screen, required by Apple's External Link Account
 * Entitlement (App Store Review Guideline 3.1.3(a) + the entitlement
 * itself).
 *
 * Rules this page MUST follow:
 *   1. ONE outbound link, single intent: open armstrongk9registry.com/billing
 *      in the system browser (Safari on iOS).
 *   2. NO "Subscribe", "Upgrade", "Buy", or pricing CTAs — they are
 *      forbidden inside the native shell.
 *   3. State is read-only. We pull the current plan and renewal date
 *      from /api/billing/my-subscription so the user understands what
 *      they're managing before they tap the external link.
 *   4. The external link MUST open in the system browser. We don't
 *      open it inside an SFSafariViewController-style in-app webview
 *      because Apple's entitlement guidance is explicit: the link
 *      transports the user OUT of the app to the web.
 *
 * This screen is visible on web too (acts as a friendly subscription
 * summary), but its sole reason for existing is to satisfy the Apple
 * review for the External Link Account Entitlement.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { isNativeApp } from "@/lib/nativeBridge";
import NativePlanNotice from "@/components/NativePlanNotice";
import { ArrowSquareOutIcon as ArrowSquareOut, ShieldCheckIcon as ShieldCheck, CreditCardIcon as CreditCard, CalendarIcon as Calendar, EnvelopeSimpleIcon as EnvelopeSimple, SparkleIcon as Sparkle, StorefrontIcon as Storefront } from "@phosphor-icons/react";

const WEB_BILLING_URL = "https://armstrongk9registry.com/billing";
const WEB_SUPPORT_URL = "https://armstrongk9registry.com/contact";

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

function PlanRow({ icon: Icon, label, value, testId }) {
  return (
    <div
      className="flex items-start gap-3 py-3 border-b border-stone-100 last:border-0"
      data-testid={testId}
    >
      <span className="h-9 w-9 rounded-lg bg-stone-50 flex items-center justify-center flex-shrink-0">
        <Icon size={18} weight="duotone" className="text-stone-700" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wider text-stone-500 mb-0.5">
          {label}
        </p>
        <p className="text-sm font-medium text-stone-900 break-words">
          {value}
        </p>
      </div>
    </div>
  );
}

export default function AccountSubscription() {
  // App Review 3.1.1: no subscription references at all inside the
  // native shell — show the neutral account notice instead.
  if (isNativeApp()) return <NativePlanNotice />;
  return <AccountSubscriptionWeb />;
}

function AccountSubscriptionWeb() {
  const navigate = useNavigate();
  const native = isNativeApp();
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);
  const [buyerPass, setBuyerPass] = useState(null);

  useEffect(() => {
    api
      .get("/billing/my-subscription")
      .then((r) => {
        setSub(r.data?.subscription || null);
        setBuyerPass(r.data?.buyer_pass || null);
      })
      .catch(() => {
        // Failure is silent — user can still tap the external link.
      })
      .finally(() => setLoading(false));
  }, []);

  const openWebBilling = () => {
    // Always open in the system browser (Safari on iOS). target=_blank
    // + noopener forces the OS to hand the URL to the default browser
    // when running inside Capacitor.
    window.open(WEB_BILLING_URL, "_blank", "noopener,noreferrer");
  };

  const hasActiveSub = sub && sub.status === "active";
  const planLabel = sub?.tier_label || sub?.tier || (hasActiveSub ? "Active" : "No active subscription");

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <header className="mb-8" data-testid="account-sub-header">
          <h1 className="font-spectral text-4xl tracking-tight mb-2">
            Your subscription
          </h1>
          <p className="text-sm text-stone-600">
            View and manage your Armstrong K9 Registry plan. Plan changes,
            payment methods, and invoices are handled on the web for
            consistency across web and mobile.
          </p>
        </header>

        {/* Current plan card */}
        <section
          className="rounded-2xl border border-[#E5E5E5] bg-white p-6 mb-6"
          data-testid="account-sub-current-card"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-spectral text-2xl tracking-tight">
              Current plan
            </h2>
            {hasActiveSub && (
              <Badge
                className="bg-emerald-600 text-white"
                data-testid="account-sub-active-badge"
              >
                Active
              </Badge>
            )}
          </div>

          {loading ? (
            <div className="space-y-3" data-testid="account-sub-loading">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-3/4" />
            </div>
          ) : (
            <div data-testid="account-sub-details">
              <PlanRow
                icon={Sparkle}
                label="Plan"
                value={planLabel}
                testId="account-sub-plan-row"
              />
              {sub?.current_period_end && (
                <PlanRow
                  icon={Calendar}
                  label={
                    sub?.cancel_at_period_end
                      ? "Access ends"
                      : "Renews on"
                  }
                  value={formatDate(sub.current_period_end)}
                  testId="account-sub-renewal-row"
                />
              )}
              {sub?.price_label && (
                <PlanRow
                  icon={CreditCard}
                  label="Price"
                  value={sub.price_label}
                  testId="account-sub-price-row"
                />
              )}
              {buyerPass?.active && (
                <PlanRow
                  icon={ShieldCheck}
                  label="Buyer Pass"
                  value={
                    buyerPass.expires_at
                      ? `Active until ${formatDate(buyerPass.expires_at)}`
                      : "Active"
                  }
                  testId="account-sub-buyer-pass-row"
                />
              )}
              {!hasActiveSub && !buyerPass?.active && (
                <p
                  className="text-sm text-stone-600 py-3"
                  data-testid="account-sub-no-plan"
                >
                  You don&apos;t have an active subscription. You can browse
                  the registry, view public pages, and sign in to existing
                  features. To start a plan, manage your account on the web.
                </p>
              )}
            </div>
          )}
        </section>

        {/* The single external-link action */}
        <section
          className="rounded-2xl border-2 border-[#C9A227] bg-[#FFFBEB] p-6 mb-6"
          data-testid="account-sub-manage-on-web-card"
        >
          <div className="flex items-start gap-4">
            <span className="h-12 w-12 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
              <Storefront size={24} weight="duotone" className="text-[#8A6A00]" />
            </span>
            <div className="flex-1 min-w-0">
              <h3 className="font-spectral text-xl tracking-tight mb-1">
                Manage subscription on the web
              </h3>
              <p className="text-sm text-stone-700 mb-4 leading-relaxed">
                Plan changes, payment methods, invoices, and cancellation
                are handled at <span className="font-mono">armstrongk9registry.com/billing</span>.{" "}
                {native ? "Tapping below opens your default browser." : "Click below to open it now."}
              </p>
              <Button
                type="button"
                onClick={openWebBilling}
                className="w-full sm:w-auto bg-[#0E0E0E] text-white hover:bg-[#1a1a1a] h-11 px-6 rounded-full"
                data-testid="account-sub-manage-on-web-btn"
              >
                <ArrowSquareOut size={16} weight="bold" className="mr-2" />
                Open armstrongk9registry.com/billing
              </Button>
            </div>
          </div>
        </section>

        {/* Support */}
        <section
          className="rounded-2xl border border-[#E5E5E5] bg-white p-6 mb-6"
          data-testid="account-sub-support-card"
        >
          <div className="flex items-start gap-4">
            <span className="h-10 w-10 rounded-lg bg-stone-50 flex items-center justify-center flex-shrink-0">
              <EnvelopeSimple size={20} weight="duotone" className="text-stone-700" />
            </span>
            <div className="flex-1 min-w-0">
              <h3 className="font-spectral text-lg tracking-tight mb-1">
                Need help with billing?
              </h3>
              <p className="text-sm text-stone-600 mb-3 leading-relaxed">
                Our team responds within one business day. Refunds, plan
                disputes, and access issues are handled on the web.
              </p>
              <a
                href={WEB_SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-[#0E0E0E] underline hover:text-[#C9A227]"
                data-testid="account-sub-support-link"
              >
                Contact support &rarr;
              </a>
            </div>
          </div>
        </section>

        {/* Quiet back link */}
        <div className="text-center">
          <Link
            to="/dashboard"
            className="text-sm text-stone-500 hover:text-stone-800 underline"
            data-testid="account-sub-back-to-dashboard"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    </AppShell>
  );
}
